import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Search, Trash2, X, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import type {
  ExerciseRunParticipant,
  ExerciseRunUserSearchItem,
  InjectRole,
  ParticipantAccessMode,
} from '@/lib/api/v2';
import type { IExerciseInfrastructureInstance } from '@/types/scenarios';
import { v2Api } from '@/lib/api';

interface ParticipantsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  participants: ExerciseRunParticipant[];
  exerciseId: string;
  runId: number;
  onRefresh: () => Promise<void> | void;
  onManageParticipants?: () => void;
  /** When true, adding participants is locked; editing must avoid remove+add. */
  participantsLocked?: boolean;
  /** Exercise infrastructure instances for read-only VM/instance selection. */
  instances?: IExerciseInfrastructureInstance[];
}

export function ParticipantsSidebar({
  isOpen,
  onClose,
  participants,
  exerciseId,
  runId,
  onRefresh,
  onManageParticipants,
  participantsLocked,
  instances,
}: ParticipantsSidebarProps) {
  const total = participants.length;
  const list = participants ?? [];
  const locked = Boolean(participantsLocked);
  const infraInstances = instances ?? [];
  // When locked (e.g. participants_added), we make edit/delete fully read-only.
  const fullyReadOnly = locked;

  const getParticipantUserId = (p: ExerciseRunParticipant): number | undefined =>
    // Some run payloads return nested user instead of flat user_id/email/full_name.
    p.user_id ?? p.user?.id;

  const getParticipantEmail = (p: ExerciseRunParticipant): string =>
    p.user_email ?? p.user?.email ?? '';

  const getParticipantFullName = (p: ExerciseRunParticipant): string =>
    p.user_full_name ?? p.user?.full_name ?? '';

  const getParticipantRoleId = (p: ExerciseRunParticipant): number | undefined =>
    // Newer payloads may return role/team as nested `team`.
    (p.role_id as unknown as number | undefined) ?? (p.team?.id as number | undefined);

  const getParticipantRoleName = (p: ExerciseRunParticipant): string =>
    p.role_name ?? p.team?.name ?? '';

  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ExerciseRunParticipant | null>(null);
  const [editAccessMode, setEditAccessMode] = useState<ParticipantAccessMode>('ttx_only');
  const [editAccessLocked, setEditAccessLocked] = useState(false);
  const [editRoleOptions, setEditRoleOptions] = useState<InjectRole[]>([]);
  const [editRolesLoading, setEditRolesLoading] = useState(false);
  const [editRoleId, setEditRoleId] = useState<string>('');
  const [editUser, setEditUser] = useState<ExerciseRunUserSearchItem | null>(null);
  const [editUserQuery, setEditUserQuery] = useState('');
  const [editUserSearchActivated, setEditUserSearchActivated] = useState(false);
  const [editUserLoading, setEditUserLoading] = useState(false);
  const [editUserResults, setEditUserResults] = useState<ExerciseRunUserSearchItem[]>([]);
  const [editSaving, setEditSaving] = useState(false);

  const [editComputeInstanceId, setEditComputeInstanceId] = useState<string>('');

  const selectedEditTeamId = useMemo(() => {
    if (!editRoleId) return null;
    const n = Number(editRoleId);
    return Number.isFinite(n) ? n : null;
  }, [editRoleId]);

  const takenInstanceIds = useMemo(() => {
    const out = new Set<string>();
    for (const p of participants ?? []) {
      if (p.assigned_compute_instance_id) out.add(String(p.assigned_compute_instance_id));
    }
    return out;
  }, [participants]);

  const instanceAssigneeById = useMemo(() => {
    const out = new Map<string, string>();
    for (const p of participants ?? []) {
      if (!p.assigned_compute_instance_id) continue;
      const iid = String(p.assigned_compute_instance_id);
      out.set(
        iid,
        getParticipantFullName(p) || getParticipantEmail(p) || `User #${getParticipantUserId(p) ?? ''}`,
      );
    }
    return out;
  }, [participants]);

  const availableInstancesForEditTeam = useMemo(() => {
    const teamId = selectedEditTeamId;
    if (!teamId) return [];

    return (infraInstances ?? []).filter((i) => Number(i.team_id) === teamId);
  }, [infraInstances, selectedEditTeamId]);

  const currentAssignedInstanceId = editTarget?.assigned_compute_instance_id
    ? String(editTarget.assigned_compute_instance_id)
    : '';
  const currentAssignedInstanceName =
    editTarget?.assigned_compute_instance_name ?? currentAssignedInstanceId;

  const instancesForEditTeamWithCurrent = useMemo(() => {
    const base = availableInstancesForEditTeam ?? [];
    if (!currentAssignedInstanceId) return base;

    const hasCurrent = base.some((i) => String(i.id) === currentAssignedInstanceId);
    if (hasCurrent) return base;

    // If infraInstances doesn't include the currently assigned instance (common in locked state),
    // we still show it so the dropdown can be pre-filled.
    return [
      ...base,
      {
        id: currentAssignedInstanceId as any,
        name: currentAssignedInstanceName,
        team_id: selectedEditTeamId ?? (editTarget ? (getParticipantRoleId(editTarget) as any) : undefined),
        team_name: undefined,
        power_state: undefined,
      } as any,
    ];
  }, [
    availableInstancesForEditTeam,
    currentAssignedInstanceId,
    currentAssignedInstanceName,
    selectedEditTeamId,
    editTarget,
  ]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ExerciseRunParticipant | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);

  const accessModeOptions = [
    { value: 'ttx_only', label: 'TTX only — injects / narrative' },
    { value: 'vm_only', label: 'VM only — console / lab infrastructure' },
    { value: 'combined', label: 'VM + TTX — both' },
  ] as const;

  const resolveDefaultAccessMode = (p: ExerciseRunParticipant): ParticipantAccessMode => {
    if (p.access_mode) return p.access_mode;
    const hasVm = Boolean(p.has_vm_access);
    const hasTtx = Boolean(p.has_ttx_access);
    if (hasVm && hasTtx) return 'combined';
    if (hasVm) return 'vm_only';
    return 'ttx_only';
  };

  const userSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const editRoleIdRef = useRef<string>('');

  useEffect(() => {
    editRoleIdRef.current = editRoleId;
  }, [editRoleId]);

  // Prefill when edit opens or target changes.
  useEffect(() => {
    if (!editOpen || !editTarget) return;

    const initialMode = resolveDefaultAccessMode(editTarget);
    setEditAccessMode(initialMode);
    setEditAccessLocked(false);

    const rid = getParticipantRoleId(editTarget);
    setEditRoleId(rid != null ? String(rid) : '');
    // Prefill VM assignment when in VM mode (nested payload supported by backend).
    setEditComputeInstanceId(editTarget.assigned_compute_instance_id ?? '');
    // Include current role so the dropdown can display immediately.
    setEditRoleOptions([
      {
        id: rid ?? 0,
        code: '',
        name: getParticipantRoleName(editTarget),
        order: 0,
      } as InjectRole,
    ]);
    setEditRolesLoading(false);

    setEditUser({
      id: getParticipantUserId(editTarget) ?? 0,
      email: getParticipantEmail(editTarget),
      full_name: getParticipantFullName(editTarget),
      expected_action: '',
    });

    // Prefill the search box, but we only start searching once the user types.
    setEditUserQuery(getParticipantFullName(editTarget) || getParticipantEmail(editTarget));
    setEditUserSearchActivated(false);
    setEditUserResults([]);
    setEditUserLoading(false);
    setEditSaving(false);
  }, [editOpen, editTarget]);

  // When switching to TTX-only, clear VM/instance selection so the payload stays consistent.
  useEffect(() => {
    if (!editOpen) return;
    if (editAccessMode === 'ttx_only') setEditComputeInstanceId('');
  }, [editAccessMode, editOpen]);

  // Load role options for selected access mode.
  useEffect(() => {
    if (!editOpen || !editTarget) return;

    let cancelled = false;
    (async () => {
      setEditRolesLoading(true);
      try {
        const res = await v2Api.dashboard.exerciseRuns.participantRoleOptions(
          exerciseId,
          runId,
          editAccessMode,
        );
        if (cancelled) return;

        setEditAccessLocked(Boolean(res.access_mode_locked));
        // If access is locked, backend may force a different effective mode.
        if (res.access_mode_locked && res.effective_access_mode && res.effective_access_mode !== editAccessMode) {
          setEditAccessMode(res.effective_access_mode);
        }

        const fetchedRoles = Array.isArray(res.roles) ? res.roles : [];
        const currentRoleId = String(getParticipantRoleId(editTarget) ?? '');
        const currentRoleOption = {
          id: getParticipantRoleId(editTarget) as any,
          code: '',
          name: getParticipantRoleName(editTarget),
          order: 0,
        } as InjectRole;

        // Keep the participant's existing role selected.
        // If backend doesn't return it for this access mode, we still include it so VM/Instance filtering stays correct.
        if (fetchedRoles.length > 0) {
          const availableRoleIds = new Set(fetchedRoles.map((r) => String(r.id)));
          const mergedRoles =
            availableRoleIds.has(currentRoleId) ? fetchedRoles : [...fetchedRoles, currentRoleOption];
          setEditRoleOptions(mergedRoles);
          setEditRoleId(currentRoleId);
        }
      } catch {
        if (!cancelled) {
          // Keep prefilled role selection so the dropdown still shows the current value.
          setEditAccessLocked(false);
        }
      } finally {
        if (!cancelled) setEditRolesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Intentionally depend on editRoleId too, because we may need to validate it against fetched roles.
  }, [editOpen, exerciseId, runId, editAccessMode, editTarget]);

  // Search users for the new selection.
  useEffect(() => {
    if (!editOpen || !editTarget) return;
    if (!editUserSearchActivated) return;

    const q = editUserQuery.trim();
    // Keep it simple: require min chars so we don't hammer the API.
    if (q.length < 2) {
      setEditUserResults([]);
      setEditUserLoading(false);
      return;
    }

    if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current);

    setEditUserLoading(true);
    userSearchDebounceRef.current = setTimeout(async () => {
      try {
        const roleIdOpt = editRoleId ? String(editRoleId) : undefined;
        const list = await v2Api.dashboard.exerciseRuns.participants.searchUsers(
          exerciseId,
          runId,
          q,
          false,
          roleIdOpt,
        );
        setEditUserResults(Array.isArray(list) ? list : []);
      } catch {
        setEditUserResults([]);
      } finally {
        setEditUserLoading(false);
      }
    }, 300);

    return () => {
      if (userSearchDebounceRef.current) clearTimeout(userSearchDebounceRef.current);
    };
  }, [editOpen, editTarget, editUserSearchActivated, editUserQuery, editRoleId, exerciseId, runId]);

  const canSaveEdit = Boolean(
    editTarget &&
      !editSaving &&
      !editRolesLoading &&
      // For VM modes we must pick an instance/VM id.
      (editAccessMode === 'ttx_only'
        ? Boolean(locked ? editAccessMode : editUser && editRoleId)
        : Boolean(editComputeInstanceId) && (locked ? Boolean(editAccessMode) : Boolean(editUser && editRoleId))),
  );

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-200"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-card border-l border-border shadow-lg z-50 flex flex-col overflow-hidden transition-transform duration-200">
        <div className="flex items-center justify-between border-b border-border p-4 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center">
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <h2 className="font-semibold truncate">Participants</h2>
              <p className="text-xs text-muted-foreground">{total} total</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={() => {
                onManageParticipants?.();
              }}
            >
              Add
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {list.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground text-sm">
              No participants yet. Add them from <span className="font-medium text-foreground">Add</span>.
            </p>
          ) : (
            <ul className="space-y-2">
              {list.map((p) => {
                const accessBits = [p.has_vm_access ? 'VM' : null, p.has_ttx_access ? 'TTX' : null]
                  .filter(Boolean)
                  .join(' · ');

                const displayName = getParticipantFullName(p) || getParticipantEmail(p) || `User #${getParticipantUserId(p)}`;

                return (
                  <li
                    key={p.id}
                    className="rounded-lg border bg-muted/20 px-3 py-2 space-y-1"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {displayName}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {getParticipantEmail(p) || ''}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <Badge variant="secondary" className="font-normal">
                          {getParticipantRoleName(p)}
                        </Badge>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            disabled={fullyReadOnly}
                            onClick={() => {
                              setEditTarget(p);
                              setEditAccessMode(resolveDefaultAccessMode(p));
                              setEditOpen(true);
                            }}
                            aria-label={`Edit participant access for ${displayName}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            disabled={fullyReadOnly}
                            onClick={() => {
                              setDeleteTarget(p);
                              setDeleteOpen(true);
                            }}
                            aria-label={`Delete participant ${displayName}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Access: {accessBits || '—'}
                    </p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Edit participant */}
        <Dialog
          open={editOpen}
          onOpenChange={(o) => {
            setEditOpen(o);
            if (!o) {
              setEditTarget(null);
              setEditSaving(false);
            }
          }}
        >
          <DialogContent className="sm:max-w-[520px]">
            <DialogHeader>
              <DialogTitle>Edit participant access</DialogTitle>
              <DialogDescription>
                {editTarget ? (
                  <>
                    {(getParticipantFullName(editTarget) || getParticipantEmail(editTarget) || `User #${getParticipantUserId(editTarget)}`)} · Role:{' '}
                    <span className="font-medium text-foreground">{getParticipantRoleName(editTarget)}</span>
                  </>
                ) : (
                  ' '
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              <Label htmlFor="edit-user-search" className="text-xs font-medium text-foreground">
                User
              </Label>
              <Input
                id="edit-user-search"
                value={editUserQuery}
                placeholder="Search users to replace this participant…"
                onChange={(e) => {
                  setEditUserSearchActivated(true);
                  setEditUserQuery(e.target.value);
                }}
                disabled={editSaving || locked}
              />
              <div className="text-[11px] text-muted-foreground">
                Selected: <span className="font-medium text-foreground">{editUser ? editUser.full_name || editUser.email : '—'}</span>
              </div>
              {editUserResults.length > 0 && (
                <div className="border rounded-md p-2 bg-muted/20">
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-2">
                    <Search className="h-3.5 w-3.5" />
                    Results
                  </div>
                  <div className="max-h-36 overflow-y-auto space-y-1">
                    {editUserResults.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        className="w-full text-left px-2 py-1 rounded hover:bg-muted/50 text-sm"
                        onClick={() => {
                          setEditUser(u);
                          setEditUserQuery(u.full_name || u.email || '');
                          setEditUserResults([]);
                          setEditUserSearchActivated(false);
                        }}
                      >
                        {u.full_name || u.email}
                        <span className="text-muted-foreground text-[11px]"> ({u.email})</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <Label htmlFor="edit-access-mode" className="text-xs font-medium text-foreground">
                Access mode
              </Label>
              <select
                id="edit-access-mode"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={editAccessMode}
                onChange={(e) => setEditAccessMode(e.target.value as ParticipantAccessMode)}
                disabled={editSaving || editAccessLocked || fullyReadOnly}
              >
                {accessModeOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {editAccessLocked && (
                <p className="text-muted-foreground text-[11px] leading-snug">
                  Access mode is fixed for this exercise based on VM availability.
                </p>
              )}

              <Label htmlFor="edit-role-select" className="text-xs font-medium text-foreground">
                Role
              </Label>
              <select
                id="edit-role-select"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={editRoleId}
                onChange={(e) => setEditRoleId(e.target.value)}
                disabled={editSaving || editRolesLoading || locked}
              >
                {editRolesLoading ? (
                  <option value="">{'Loading roles…'}</option>
                ) : (
                  <>
                    <option value="">Select role…</option>
                    {editRoleOptions.map((r) => (
                      <option key={r.id} value={String(r.id)}>
                        {r.name}
                      </option>
                    ))}
                  </>
                )}
              </select>

              {editAccessMode !== 'ttx_only' && (
                <div className="space-y-1">
                  <Label htmlFor="edit-compute-instance" className="text-xs font-medium text-foreground">
                    Instance / VM
                    <span className="text-destructive">*</span>
                  </Label>
                  <select
                    id="edit-compute-instance"
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    value={editComputeInstanceId}
                    onChange={(e) => setEditComputeInstanceId(e.target.value)}
                    disabled={editSaving || fullyReadOnly || instancesForEditTeamWithCurrent.length === 0}
                  >
                    <option value="">
                      {instancesForEditTeamWithCurrent.length === 0 ? 'No VMs mapped' : 'Select an unassigned VM…'}
                    </option>
                    {instancesForEditTeamWithCurrent.map((i) => {
                      const iid = String(i.id);
                      const currentId = editTarget?.assigned_compute_instance_id
                        ? String(editTarget.assigned_compute_instance_id)
                        : '';
                      const assignee = instanceAssigneeById.get(iid);
                      const disabledOption = Boolean(iid && takenInstanceIds.has(iid) && iid !== currentId);

                      return (
                        <option key={i.id} value={iid} disabled={disabledOption}>
                          {i.name}
                          {assignee
                            ? disabledOption
                              ? ` (assigned: ${assignee})`
                              : ' (this)'
                            : ' (unassigned)'}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              <p className="text-muted-foreground text-[11px] leading-snug">
                Edit updates the participant by removing the old assignment and adding the new one.
              </p>

              <p className="text-muted-foreground text-[11px] leading-snug">
                Changing access will keep your role. VM modes may auto-assign a free VM.
              </p>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditOpen(false)} disabled={editSaving}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={async () => {
                  if (!editTarget) return;
                  setEditSaving(true);
                  try {
                    if (locked) {
                      // Run is ready/locked: adding participants is forbidden, so update access_mode via PATCH only.
                      const uid = getParticipantUserId(editTarget);
                      if (uid == null) throw new Error('Participant user_id is missing.');
                      if (editAccessMode !== 'ttx_only') {
                        if (!editComputeInstanceId) throw new Error('Please select a VM/Instance.');
                        await v2Api.dashboard.exerciseRuns.participants.update(
                          exerciseId,
                          runId,
                          String(uid),
                          {
                            access_mode: editAccessMode,
                            compute_instance_id: editComputeInstanceId,
                          },
                        );
                      } else {
                        await v2Api.dashboard.exerciseRuns.participants.update(
                          exerciseId,
                          runId,
                          String(uid),
                          { access_mode: editAccessMode },
                        );
                      }
                    } else {
                      // Unlocked: edit may reassign role/user (remove + add).
                      const uid = getParticipantUserId(editTarget);
                      if (uid == null) throw new Error('Participant user_id is missing.');
                      await v2Api.dashboard.exerciseRuns.participants.remove(exerciseId, runId, String(uid));
                      if (!editUser) throw new Error('Missing selected user');
                      if (!editRoleId) throw new Error('Missing selected role');
                      const rid = parseInt(editRoleId, 10);
                      const addPayload: any = {
                        user_id: editUser.id,
                        role_id: rid,
                        access_mode: editAccessMode,
                      };
                      if (editAccessMode !== 'ttx_only') {
                        if (!editComputeInstanceId) throw new Error('Please select a VM/Instance.');
                        addPayload.compute_instance_id = editComputeInstanceId;
                      }
                      await v2Api.dashboard.exerciseRuns.participants.add(exerciseId, runId, addPayload);
                    }
                    await onRefresh();
                    toast.success('Participant updated');
                    setEditOpen(false);
                    setEditTarget(null);
                  } catch (e) {
                    const msg = e instanceof Error ? e.message : 'Failed to update participant';
                    toast.error(msg);
                  } finally {
                    setEditSaving(false);
                  }
                }}
                disabled={!canSaveEdit || editSaving || fullyReadOnly}
              >
                {editSaving ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete participant */}
        <AlertDialog
          open={deleteOpen}
          onOpenChange={(o) => {
            setDeleteOpen(o);
            if (!o) {
              setDeleteTarget(null);
              setDeleteSaving(false);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove participant?</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteTarget ? (
                  <>
                    This will remove{' '}
                    <span className="font-medium text-foreground">
                      {deleteTarget.user_full_name || deleteTarget.user_email || `User #${deleteTarget.user_id}`}
                    </span>{' '}
                    from the run.
                  </>
                ) : (
                  ' '
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteSaving}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => {
                  if (!deleteTarget) return;
                  setDeleteSaving(true);
                  try {
                    const uid = getParticipantUserId(deleteTarget);
                    if (uid == null) throw new Error('Participant user_id is missing.');
                    await v2Api.dashboard.exerciseRuns.participants.remove(exerciseId, runId, String(uid));
                    await onRefresh();
                    toast.success('Participant removed');
                    setDeleteOpen(false);
                    setDeleteTarget(null);
                  } catch (e) {
                    const msg = e instanceof Error ? e.message : 'Failed to remove participant';
                    toast.error(msg);
                  } finally {
                    setDeleteSaving(false);
                  }
                }}
                disabled={deleteSaving || fullyReadOnly}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteSaving ? 'Removing…' : 'Remove'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
}

